import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FilePlus2, Files, Building2, Users, ShieldCheck, Truck, Package, TrendingUp, History, Landmark,
  BarChart3, ScrollText, Settings, LogOut, Menu, X, Clock, KeyRound, PanelLeftClose, PanelLeftOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL, riyadhClock } from "@/lib/format";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = {
  super_admin: [
    ["Overview", [["/", "Dashboard", LayoutDashboard]]],
    ["Purchases", [["/bills/new", "Purchase Bill Entry", FilePlus2], ["/bills", "Purchase Bill History", Files]]],
    ["Sales & VAT", [["/sales/new", "Sales Entry", TrendingUp], ["/sales", "Sales History", History], ["/vat-consolidation", "VAT Consolidation", Landmark]]],
    ["Management", [["/clients", "Clients & Assignments", Building2], ["/staff", "Staff", Users], ["/auditors", "Auditors", ShieldCheck]]],
    ["Masters", [["/vendors", "Vendors", Truck], ["/items", "Products / Items", Package]]],
    ["System", [["/reports", "Reports", BarChart3], ["/audit-logs", "Audit Logs", ScrollText], ["/settings", "System Settings", Settings]]],
  ],
  staff: [
    ["Workspace", [["/", "Dashboard", LayoutDashboard], ["/clients", "My Assigned Clients", Building2]]],
    ["Purchases", [["/bills/new", "Purchase Bill Entry", FilePlus2], ["/bills", "Purchase Bill History", Files]]],
    ["Sales & VAT", [["/sales/new", "Sales Entry", TrendingUp], ["/sales", "Sales History", History], ["/vat-consolidation", "VAT Consolidation", Landmark]]],
    ["Masters", [["/vendors", "Vendors", Truck], ["/items", "Products / Items", Package]]],
    ["Insights", [["/reports", "Reports", BarChart3]]],
  ],
  auditor: [
    ["Audit", [["/", "Dashboard", LayoutDashboard], ["/bills", "Purchase Bill History", Files], ["/sales", "Sales History", History], ["/vat-consolidation", "VAT Consolidation", Landmark], ["/clients", "Authorised Clients", Building2]]],
    ["Insights", [["/reports", "Reports", BarChart3]]],
  ],
};

const RiyadhClock = () => {
  const [t, setT] = useState(riyadhClock());
  useEffect(() => {
    const i = setInterval(() => setT(riyadhClock()), 1000);
    return () => clearInterval(i);
  }, []);
  return (
    <div className="hidden md:flex items-center gap-2 text-xs text-slate-600 border border-slate-200 rounded px-2.5 py-1.5 bg-white" data-testid="riyadh-clock">
      <Clock className="w-3.5 h-3.5 text-emerald-700" />
      <span className="num">{t}</span>
      <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 rounded">AST · Riyadh</span>
    </div>
  );
};

const useNotifications = (role, nav) => {
  const [n, setN] = useState({ uploads: 0, sales: 0, vat: 0, clients: [] });
  const prev = useRef(null);
  useEffect(() => {
    if (role === "auditor") { return undefined; }
    const load = () => api.get("/notifications").then(({ data }) => {
      const p = prev.current;
      if (p && data.uploads > p.uploads) { toast.info(`New client bill upload${data.uploads - p.uploads > 1 ? "s" : ""} received`, { description: "Open Purchase Bill Entry → Pending bills" }); }
      if (p && data.sales > p.sales) { toast.info("New client sales submission received", { description: "Open Sales Entry to approve" }); }
      const fresh = data.clients.filter((c) => c.vat > ((p?.clients || []).find((x) => x.client_id === c.client_id)?.vat || 0));
      if (p) {
        fresh.forEach((c) => toast.success(`${c.client_name} updated a VAT report status`, {
          description: "Client marked a shared VAT report as Paid / Refund received / Filed", duration: 10000,
          action: { label: "View", onClick: () => nav(`/vat-consolidation?client=${c.client_id}`) },
        }));
      }
      prev.current = data; setN(data);
    }).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    window.addEventListener("ksavat-notif-refresh", load);
    return () => { clearInterval(t); window.removeEventListener("ksavat-notif-refresh", load); };
  }, [role, nav]);
  return n;
};

const Badge = ({ count, collapsed, testid, title }) => (count > 0 ? (
  <span title={title} data-testid={testid} className={collapsed ? "absolute top-1 right-2 w-2.5 h-2.5 rounded-full bg-amber-400 ring-2 ring-[#064E3B]" : "ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-amber-400 text-[#064E3B] text-[11px] font-bold leading-5 text-center num"}>{collapsed ? "" : count}</span>
) : null);

const Sidebar = ({ role, onNavigate, appName, collapsed, notif }) => (
  <div className="h-full flex flex-col bg-[#064E3B] text-emerald-50 relative grain">
    <div className={`${collapsed ? "px-3" : "px-5"} py-5 border-b border-white/10 relative z-10`}>
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 shrink-0 rounded bg-amber-500 flex items-center justify-center font-display font-bold text-[#064E3B] mx-auto">﷼</div>
        {!collapsed && <div className="flex-1">
          <div className="font-display font-semibold text-white leading-tight" data-testid="app-name">{appName}</div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-300">Kingdom of Saudi Arabia</div>
        </div>}
      </div>
    </div>
    <nav className={`flex-1 overflow-y-auto ${collapsed ? "px-2" : "px-3"} py-4 space-y-5 relative z-10`}>
      {NAV[role].map(([group, links]) => (
        <div key={group}>
          {collapsed ? <div className="mx-2 mb-2 border-t border-white/10" /> : <div className="px-2 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300/70">{group}</div>}
          {links.map(([to, label, Icon]) => (
            <NavLink key={to} to={to} end onClick={onNavigate} title={collapsed ? label : undefined} data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => `relative flex items-center gap-2.5 ${collapsed ? "justify-center px-0" : "px-2.5"} py-2 rounded text-sm transition-colors duration-150 ${isActive ? "bg-white text-[#064E3B] font-medium" : "text-emerald-100/90 hover:bg-white/10"}`}>
              <Icon className="w-4 h-4 shrink-0" />{!collapsed && label}
              {to === "/bills/new" && <Badge count={notif?.uploads} collapsed={collapsed} testid="nav-badge-uploads" title={(notif?.clients || []).filter((c) => c.uploads).map((c) => `${c.client_name}: ${c.uploads}`).join("\n")} />}
              {to === "/vat-consolidation" && <Badge count={notif?.vat} collapsed={collapsed} testid="nav-badge-vat" title={(notif?.clients || []).filter((c) => c.vat).map((c) => `${c.client_name}: ${c.vat} status update(s)`).join("\n")} />}
              {to === "/sales/new" && <Badge count={notif?.sales} collapsed={collapsed} testid="nav-badge-sales" title={(notif?.clients || []).filter((c) => c.sales).map((c) => `${c.client_name}: ${c.sales}`).join("\n")} />}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
    {!collapsed && <div className="px-5 py-4 text-[10px] text-emerald-300/70 border-t border-white/10 relative z-10">
      Client VAT account management. Not a ZATCA filing system.
    </div>}
  </div>
);

export default function AppShell() {
  const { user, settings, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(localStorage.getItem("ksavat_sidebar") === "1");
  const toggle = () => { localStorage.setItem("ksavat_sidebar", collapsed ? "0" : "1"); setCollapsed(!collapsed); };
  const [focus, setFocus] = useState(false);
  useEffect(() => {
    const h = (e) => setFocus(!!e.detail);
    window.addEventListener("ksavat-focus", h);
    return () => window.removeEventListener("ksavat-focus", h);
  }, []);
  const nav = useNavigate();
  const notif = useNotifications(user.role, nav);
  const appName = settings?.app_name || "KSA VAT Bill Entry";
  return (
    <div className="min-h-screen bg-slate-50">
      <aside className={`${focus ? "hidden" : "hidden lg:block"} fixed inset-y-0 left-0 z-30 transition-[width] duration-200 ${collapsed ? "w-[72px]" : "w-64"}`} data-testid="desktop-sidebar"><Sidebar role={user.role} appName={appName} collapsed={collapsed} notif={notif} /></aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64"><Sidebar role={user.role} appName={appName} notif={notif} onNavigate={() => setOpen(false)} /></div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className={`transition-[padding] duration-200 ${focus ? "" : collapsed ? "lg:pl-[72px]" : "lg:pl-64"}`}>
        <header className="sticky top-0 z-20 h-14 bg-white/80 backdrop-blur-xl border-b border-slate-200 flex items-center justify-between px-4 sm:px-6">
          <button className="lg:hidden p-2 -ml-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-button">
            {open ? <X className="w-5 h-5" /> : <span className="relative"><Menu className="w-5 h-5" />{notif.uploads + notif.sales + notif.vat > 0 && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-500" data-testid="mobile-menu-badge" />}</span>}
          </button>
          <div className="hidden lg:flex items-center gap-3">
            <button onClick={toggle} className="p-1.5 rounded hover:bg-slate-100 text-slate-600 transition-colors" title={collapsed ? "Expand menu" : "Collapse menu"} data-testid="sidebar-toggle-button">
              {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
            </button>
            <span className="text-sm text-slate-500">{settings?.company_name || ""}</span>
          </div>
          <div className="flex items-center gap-3">
            <RiyadhClock />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2.5 pl-2 pr-1 py-1 rounded hover:bg-slate-100 transition-colors" data-testid="user-menu-trigger">
                  <div className="text-right hidden sm:block">
                    <div className="text-sm font-medium text-slate-800 leading-tight">{user.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold" data-testid="current-user-role">{ROLE_LABEL[user.role]}</div>
                  </div>
                  <div className="w-8 h-8 rounded bg-emerald-800 text-white flex items-center justify-center text-xs font-semibold">
                    {user.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white">
                <DropdownMenuLabel className="text-xs text-slate-500 font-normal">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-change-password"><KeyRound className="w-4 h-4 mr-2" />Change password</DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-button" className="text-red-600"><LogOut className="w-4 h-4 mr-2" />Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className={`px-4 sm:px-6 lg:px-8 py-6 lg:py-8 ${focus ? "" : "max-w-[1600px]"}`}><Outlet /></main>
      </div>
    </div>
  );
}
